import { CheckoutFormInfo } from '@/types/joinUsType';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface EnrollmentPackItems {
  itemCode: string;
  quantity: number;
  price: string;
  image: string;
  name: string;
}

export interface EnrollmentPackViewModel {
  packItemCode: string;
  packItemName: string;
  packItemPrice: string;
  isFromPetQuiz?: boolean;
  items: EnrollmentPackItems[];
}

export type enrollmentSlice = {
  enrollmentPackViewModel: EnrollmentPackViewModel;
  totalBuildPackPrice: string;
  checkoutFormData: Partial<CheckoutFormInfo>;
};

const loadState = (): enrollmentSlice | undefined => {
  if (typeof window === 'undefined') {
    // Return undefined when running on the server
    return undefined;
  }

  try {
    const serializedState = localStorage.getItem('enrollment');
    return serializedState ? JSON.parse(serializedState) : undefined;
  } catch (error) {
    console.error('Failed to load state from localStorage:', error);
    return undefined;
  }
};

const saveState = (state: enrollmentSlice) => {
  if (typeof window === 'undefined') {
    // Skip saving state on the server
    return;
  }

  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem('enrollment', serializedState);
  } catch (error) {
    console.error('Failed to save state to localStorage:', error);
  }
};

const removeState = () => {
  localStorage.removeItem('enrollment');
};

export const enrollmentInitialState: enrollmentSlice = loadState() || {
  enrollmentPackViewModel: {
    packItemCode: '',
    packItemName: '',
    packItemPrice: '',
    items: [],
  },
  totalBuildPackPrice: '0',
  checkoutFormData: {},
};

const enrollmentSlice = createSlice({
  name: 'enrollmentSlice',
  initialState: enrollmentInitialState,
  reducers: {
    updateEnrollment(state, { payload }: PayloadAction<Partial<enrollmentSlice>>) {
      Object.assign(state, { ...payload });
      saveState(state);
    },
    updateCheckoutForm(state, { payload }: PayloadAction<Partial<CheckoutFormInfo>>) {
      state.checkoutFormData = { ...state.checkoutFormData, ...payload };
      saveState(state);
    },
    resetState: state => {
      state = enrollmentInitialState;
      removeState();
    },
  },
});

export const { updateEnrollment, updateCheckoutForm, resetState } = enrollmentSlice.actions;

export default enrollmentSlice;
